import { Flame, Lock, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { MobileShell } from "@/components/layout/MobileShell";
import { Avatar } from "@/components/ui/Avatar";
import { Card } from "@/components/ui/Card";
import { JobLogModal } from "@/components/ui/JobLogModal";
import { Pill } from "@/components/ui/Pill";
import { WeekSummaryCard } from "@/components/ui/WeekSummaryCard";
import { useObservable } from "@/hooks/useObservable";
import { useProfile } from "@/hooks/useProfile";
import { loggedDateOf } from "@/lib/loggedDate";
import {
  boundsFromWeekRecord,
  currentWeekBounds,
  DEFAULT_WEEK_START_DAY,
  formatDateOnly,
  viewedWeekLabel,
} from "@/lib/week";
import {
  computeWeekFinancials,
  findPriorValues,
  sumLogAmounts,
} from "@/lib/weekFinancials";
import { familyService } from "@/services/family.service";
import { jobsService } from "@/services/jobs.service";
import { weekLogService } from "@/services/weekLog.service";
import type { JobRecord } from "@/types/domain";

const NB_DAYS_FULL = [
  "Søndag",
  "Mandag",
  "Tirsdag",
  "Onsdag",
  "Torsdag",
  "Fredag",
  "Lørdag",
];

// Indexed by JS getDay() (0=Sunday).
const NB_DAYS_SHORT = ["Sø", "Ma", "Ti", "On", "To", "Fr", "Lø"];

function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function isSameDay(a: Date, b: Date): boolean {
  return a.toDateString() === b.toDateString();
}

export function ChildHome() {
  const { profile } = useProfile();
  const weekLogState = useObservable(weekLogService.state$, {
    week: null,
    logs: [],
    siblingTotals: {},
    isPreviousWeek: false,
    pendingPreviousWeek: null,
    allWeeks: [],
    allLogs: [],
    loading: false,
  });
  const jobs = useObservable(jobsService.jobs$, []);
  const [pendingJob, setPendingJob] = useState<JobRecord | null>(null);

  const today = startOfDay(new Date());
  const week = weekLogState.week;
  const isPreviousWeek = weekLogState.isPreviousWeek;
  const pendingPreviousWeek = weekLogState.pendingPreviousWeek;

  // Bounds for the active week. Falls back to the family's current week when
  // no week record exists yet (first-time child, no logs).
  const offset =
    familyService.snapshot.settings?.week_start_day ?? DEFAULT_WEEK_START_DAY;
  const activeBounds = week
    ? boundsFromWeekRecord(week.data)
    : currentWeekBounds(offset, today);
  const activeLength = activeBounds.length_days;

  const weekDays = Array.from({ length: activeLength }, (_, i) => {
    const d = new Date(activeBounds.start);
    d.setDate(activeBounds.start.getDate() + i);
    return startOfDay(d);
  });

  // Default selected day: today if today is in the active week, else
  // Sunday (last day) of the active week. Reset whenever the active week
  // changes (e.g. previous-week lockout resolved).
  const defaultDayForWeek = (): Date => {
    const todayInWeek = weekDays.some((d) => isSameDay(d, today));
    return todayInWeek ? today : startOfDay(activeBounds.end);
  };
  const [selectedDate, setSelectedDate] = useState<Date>(defaultDayForWeek);

  const weekId = week?.id ?? null;
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset selection only when the active week changes
  useEffect(() => {
    setSelectedDate(defaultDayForWeek());
  }, [weekId]);

  if (!profile) return null;

  const weekTotal = weekLogService.weekTotal();
  const streak = week?.data.streak ?? 0;
  const todayName = NB_DAYS_FULL[new Date().getDay()];
  const weekLocked = week !== null && week.data.status !== "open";
  const headerWeekNumber = week?.data.iso_week ?? null;

  const recentlyClosedWeek = (() => {
    if (!profile) return null;
    const cutoff = Date.now() - 24 * 60 * 60 * 1000;
    const candidates = weekLogState.allWeeks
      .filter(
        (w) =>
          w.data.status === "closed" &&
          w.data.closed_at != null &&
          new Date(w.data.closed_at).getTime() >= cutoff,
      )
      .sort((a, b) =>
        (b.data.closed_at ?? "").localeCompare(a.data.closed_at ?? ""),
      );
    return candidates[0] ?? null;
  })();

  const recentlyClosedFinancials = (() => {
    if (!recentlyClosedWeek || !profile) return null;
    const weekLogs = weekLogState.allLogs.filter(
      (l) => l.data.week_id === recentlyClosedWeek.id,
    );
    const priors = findPriorValues(
      weekLogState.allWeeks,
      profile.id,
      recentlyClosedWeek.data.iso_week,
      recentlyClosedWeek.data.year,
    );
    return computeWeekFinancials({
      earned: sumLogAmounts(weekLogs),
      bankAfter: recentlyClosedWeek.data.bank_balance_after,
      stockAfter: recentlyClosedWeek.data.stock_value_after,
      totalInvested: recentlyClosedWeek.data.total_invested,
      bankBefore: priors.bankBefore,
      stockBefore: priors.stockBefore,
      totalInvestedBefore: priors.totalInvestedBefore,
    });
  })();

  const isToday = isSameDay(selectedDate, today);
  const selectedDayLogs = weekLogState.logs.filter(
    (l) => loggedDateOf(l) === formatDateOnly(selectedDate),
  );
  const selectedDayTotal = selectedDayLogs.reduce(
    (sum, l) => sum + l.data.amount,
    0,
  );
  const selectedDayName = NB_DAYS_FULL[selectedDate.getDay()];

  const maxBackdateDays =
    familyService.snapshot.settings?.max_backdate_days ?? 1;
  const selectedDayDaysBack = Math.round(
    (today.getTime() - selectedDate.getTime()) / 86400000,
  );
  const selectedDayIsFuture = selectedDate > today;
  const selectedDayIsTooFarBack =
    !selectedDayIsFuture && selectedDayDaysBack > maxBackdateDays;
  const blockedReason: "closed-week" | "too-far-back" | "future" | null =
    weekLocked
      ? "closed-week"
      : selectedDayIsFuture
        ? "future"
        : selectedDayIsTooFarBack
          ? "too-far-back"
          : null;

  return (
    <MobileShell>
      <div style={{ padding: "52px 20px 20px" }}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 18,
          }}
        >
          <div>
            <div
              data-testid="home-period-label"
              style={{
                fontSize: 12,
                color: "var(--ink-3)",
                textTransform: "uppercase",
                letterSpacing: 0.7,
                fontWeight: 700,
              }}
            >
              {isPreviousWeek ? "Forrige uke" : todayName} · Uke{" "}
              {headerWeekNumber ?? activeBounds.iso_week}
            </div>
            <div
              style={{
                fontFamily: "var(--font-display)",
                fontStyle: "italic",
                fontSize: 30,
                fontWeight: 400,
                marginTop: 2,
                letterSpacing: -0.6,
              }}
            >
              Hei, {profile.data.name}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {pendingPreviousWeek && (
              <Pill
                tone="warn"
                size="sm"
                data-testid="pending-previous-week-pill"
              >
                Uke {pendingPreviousWeek.data.iso_week} venter
              </Pill>
            )}
            {streak > 0 && (
              <Pill tone="butter" size="sm">
                <Flame size={12} color="#8B6420" fill="#8B6420" /> {streak} d
                strek
              </Pill>
            )}
            <Avatar person={profile} size={40} />
          </div>
        </div>

        {/* Previous-week lockout banner */}
        {isPreviousWeek && week && (
          <div
            data-testid="previous-week-banner"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              background: "#F9D6CD",
              border: "1px solid var(--bad)",
              borderRadius: "var(--r-md)",
              padding: "12px 14px",
              marginBottom: 14,
              fontSize: 13,
              color: "var(--bad)",
              fontWeight: 600,
              lineHeight: 1.35,
            }}
          >
            <Lock size={18} color="var(--bad)" />
            <span>
              Forrige uke (uke {week.data.iso_week}) må bekreftes før du kan
              starte denne uka.
            </span>
          </div>
        )}

        {/* Post-close summary callout (24h after parent closes) */}
        {recentlyClosedWeek && recentlyClosedFinancials && (
          <div data-testid="week-summary-callout" style={{ marginBottom: 14 }}>
            <WeekSummaryCard
              financials={recentlyClosedFinancials}
              title={`Uke ${recentlyClosedWeek.data.iso_week} oppsummering`}
              data-testid="week-summary-callout-card"
            />
          </div>
        )}

        {/* Day tabs */}
        <div
          style={{
            display: "flex",
            gap: 6,
            marginBottom: 14,
          }}
        >
          {weekDays.map((day) => {
            const isSelected = isSameDay(day, selectedDate);
            const isDayToday = isSameDay(day, today);
            const isFuture = day > today;
            const dayDaysBack = Math.round(
              (today.getTime() - day.getTime()) / 86400000,
            );
            const isDayUnloggablePast =
              !isFuture &&
              !isDayToday &&
              (weekLocked || dayDaysBack > maxBackdateDays);
            const hasLogs = weekLogState.logs.some(
              (l) => loggedDateOf(l) === formatDateOnly(day),
            );
            const label = NB_DAYS_SHORT[day.getDay()];
            const selectedAccent = isDayToday
              ? "var(--good)"
              : isDayUnloggablePast
                ? "var(--ink-3)"
                : "var(--butter)";
            return (
              <button
                key={day.toISOString()}
                type="button"
                data-testid="day-tab"
                data-selected={String(isSelected)}
                data-today={String(isDayToday)}
                data-unloggable={String(isDayUnloggablePast)}
                disabled={isFuture}
                onClick={() => setSelectedDate(startOfDay(day))}
                style={{
                  flex: 1,
                  padding: "8px 4px",
                  borderRadius: "var(--r-md)",
                  border: isSelected
                    ? `2px solid ${selectedAccent}`
                    : "2px solid transparent",
                  background: isSelected ? selectedAccent : "var(--bg-2)",
                  color: isSelected
                    ? isDayToday
                      ? "#fff"
                      : isDayUnloggablePast
                        ? "#fff"
                        : "var(--ink)"
                    : isFuture
                      ? "var(--ink-3)"
                      : isDayUnloggablePast
                        ? "var(--ink-3)"
                        : isDayToday
                          ? "var(--ink)"
                          : "var(--ink-2)",
                  fontWeight: isSelected || isDayToday ? 700 : 500,
                  fontSize: 13,
                  opacity: isFuture ? 0.35 : 1,
                  cursor: isFuture ? "default" : "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                {label}
                <div
                  style={{
                    width: 5,
                    height: 5,
                    borderRadius: "50%",
                    background: hasLogs
                      ? isSelected
                        ? isDayToday
                          ? "rgba(255,255,255,0.7)"
                          : isDayUnloggablePast
                            ? "rgba(255,255,255,0.7)"
                            : "rgba(31,42,36,0.5)"
                        : isDayToday
                          ? "var(--good)"
                          : isDayUnloggablePast
                            ? "var(--ink-3)"
                            : "var(--butter)"
                      : "transparent",
                  }}
                />
              </button>
            );
          })}
        </div>

        {/* Hero */}
        {(() => {
          const isSelectedUnloggablePast =
            !isToday &&
            (blockedReason === "closed-week" ||
              blockedReason === "too-far-back");
          const heroBg = isToday
            ? "var(--good)"
            : isSelectedUnloggablePast
              ? "var(--ink-3)"
              : "var(--butter)";
          const heroFg =
            isToday || isSelectedUnloggablePast ? "#fff" : "var(--ink)";
          const heroFgMuted = isToday
            ? "#D8E3D3"
            : isSelectedUnloggablePast
              ? "#D8D9D2"
              : "#8B6420";
          return (
            <div
              data-testid="hero-card"
              data-variant={
                isToday
                  ? "today"
                  : isSelectedUnloggablePast
                    ? "unloggable-past"
                    : "past"
              }
              style={{
                background: heroBg,
                borderRadius: "var(--r-xl)",
                padding: "18px 20px",
                marginBottom: 14,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                color: heroFg,
              }}
            >
              <div>
                <div
                  data-testid="hero-label"
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: 0.8,
                    color: heroFgMuted,
                  }}
                >
                  {isToday ? "Tjent i dag" : selectedDayName}
                </div>
                <div
                  data-testid="hero-day-total"
                  className="num"
                  style={{
                    fontSize: 36,
                    fontWeight: 700,
                    letterSpacing: -1,
                    marginTop: 2,
                    lineHeight: 1,
                  }}
                >
                  {selectedDayTotal}{" "}
                  <span
                    style={{
                      fontSize: 16,
                      fontWeight: 500,
                      color: heroFgMuted,
                    }}
                  >
                    kr
                  </span>
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div
                  data-testid="week-total-label"
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: 0.8,
                    color: heroFgMuted,
                  }}
                >
                  {viewedWeekLabel(
                    isPreviousWeek,
                    headerWeekNumber ?? activeBounds.iso_week,
                  )}
                </div>
                <div
                  data-testid="week-total"
                  className="num"
                  style={{
                    fontSize: 22,
                    fontWeight: 700,
                    marginTop: 2,
                  }}
                >
                  {weekTotal} kr
                </div>
              </div>
            </div>
          );
        })()}

        {/* Jobs */}
        {blockedReason !== null ? (
          <div style={{ marginBottom: 18 }}>
            <BlockedDayBanner reason={blockedReason} />
          </div>
        ) : (
          <div style={{ marginBottom: 18 }}>
            <SectionTitle>Logg en jobb</SectionTitle>
            <Card padding={0} style={{ overflow: "hidden" }}>
              {jobs
                .filter((j) => j.data.kind === "regular")
                .map((job) => {
                  const jobLogs = weekLogState.logs.filter(
                    (l) => l.data.job_id === job.id,
                  );
                  const selectedDayCount = jobLogs.filter(
                    (l) => loggedDateOf(l) === formatDateOnly(selectedDate),
                  ).length;
                  const weekCount = jobLogs.length;
                  const loggedToday = jobLogs.some(
                    (l) => loggedDateOf(l) === formatDateOnly(today),
                  );
                  const maxPerDay = job.data.max_per_day;
                  const maxPerWeek = job.data.max_per_week;
                  const isLimitReached =
                    (maxPerDay !== null && selectedDayCount >= maxPerDay) ||
                    (maxPerWeek !== null && weekCount >= maxPerWeek);
                  return {
                    job,
                    selectedDayCount,
                    weekCount,
                    loggedToday,
                    isLimitReached,
                  };
                })
                .sort(
                  (a, b) => Number(a.isLimitReached) - Number(b.isLimitReached),
                )
                .map(
                  (
                    { job, selectedDayCount, weekCount, loggedToday },
                    i,
                    arr,
                  ) => (
                    <JobRow
                      key={job.id}
                      job={job}
                      selectedDayCount={selectedDayCount}
                      weekCount={weekCount}
                      loggedToday={loggedToday}
                      selectedDate={selectedDate}
                      isLast={i === arr.length - 1}
                      weekLocked={weekLocked}
                      onRequestModal={setPendingJob}
                    />
                  ),
                )}
            </Card>
          </div>
        )}

        {/* Log for selected day */}
        <div>
          <SectionTitle>Logg</SectionTitle>
          {selectedDayLogs.length > 0 ? (
            <Card padding={0} style={{ overflow: "hidden" }}>
              {selectedDayLogs.map((log, i) => (
                <div
                  key={log.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "14px 16px",
                    borderBottom:
                      i < selectedDayLogs.length - 1
                        ? "1px solid var(--line)"
                        : "none",
                  }}
                >
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 12,
                      background: "var(--bg-2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 20,
                    }}
                  >
                    {log.data.job_snapshot.emoji}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 15 }}>
                      {log.data.job_snapshot.title}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--ink-3)" }}>
                      {log.data.logged_time}
                    </div>
                  </div>
                  <div
                    className="num"
                    style={{
                      fontWeight: 700,
                      color: "var(--good)",
                      marginRight: 12,
                    }}
                  >
                    +{log.data.amount} kr
                  </div>
                  {!weekLocked && (
                    <button
                      type="button"
                      data-testid="remove-log-button"
                      onClick={() => weekLogService.removeLog(log.id)}
                      style={{ padding: 4 }}
                    >
                      <Trash2 size={22} color="var(--bad)" />
                    </button>
                  )}
                </div>
              ))}
            </Card>
          ) : (
            <div
              style={{
                textAlign: "center",
                padding: "24px 0",
                color: "var(--ink-3)",
                fontSize: 14,
              }}
            >
              Ingen jobber logget
            </div>
          )}
        </div>
      </div>
      {pendingJob && (
        <JobLogModal
          job={pendingJob}
          onSubmit={async ({ note, amount }) => {
            await weekLogService.logJob(
              pendingJob,
              note,
              selectedDate,
              amount ?? undefined,
            );
            setPendingJob(null);
          }}
          onCancel={() => setPendingJob(null)}
        />
      )}
    </MobileShell>
  );
}

function BlockedDayBanner({
  reason,
}: {
  reason: "closed-week" | "too-far-back" | "future";
}) {
  const config = {
    "closed-week": {
      testId: "week-locked-banner",
      message:
        "Uka er bekreftet og sendt til foreldrene. Du kan ikke logge flere jobber nå.",
    },
    "too-far-back": {
      testId: "too-far-back-banner",
      message: "Du kan ikke logge så langt tilbake. Spør en voksen.",
    },
    future: {
      testId: "future-day-banner",
      message: "Du kan ikke logge fram i tid.",
    },
  }[reason];
  return (
    <div
      data-testid={config.testId}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        background: "#F6E6C2",
        border: "1px solid var(--warn)",
        borderRadius: "var(--r-md)",
        padding: "12px 14px",
        fontSize: 13,
        color: "var(--warn)",
        fontWeight: 600,
        lineHeight: 1.35,
      }}
    >
      <Lock size={18} color="var(--warn)" />
      <span>{config.message}</span>
    </div>
  );
}

function SectionTitle({ children }: { children: string }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div
        style={{
          fontSize: 12,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: 0.8,
          color: "var(--ink-2)",
        }}
      >
        {children}
      </div>
    </div>
  );
}

function JobRow({
  job,
  selectedDayCount,
  weekCount,
  loggedToday,
  selectedDate,
  isLast,
  weekLocked,
  onRequestModal,
}: {
  job: JobRecord;
  selectedDayCount: number;
  weekCount: number;
  loggedToday: boolean;
  selectedDate: Date;
  isLast: boolean;
  weekLocked: boolean;
  onRequestModal: (job: JobRecord) => void;
}) {
  const [logging, setLogging] = useState(false);

  const maxPerDay = job.data.max_per_day;
  const maxPerWeek = job.data.max_per_week;
  const isAtDailyLimit = maxPerDay !== null && selectedDayCount >= maxPerDay;
  const isAtWeeklyLimit = maxPerWeek !== null && weekCount >= maxPerWeek;
  const isLimitReached = isAtDailyLimit || isAtWeeklyLimit;

  const limitParts: string[] = [];
  if (maxPerDay !== null)
    limitParts.push(`${selectedDayCount}/${maxPerDay} dag`);
  if (maxPerWeek !== null) limitParts.push(`${weekCount}/${maxPerWeek} uke`);
  const limitLabel = limitParts.join(" · ");

  const needsModal = job.data.require_comment || job.data.price === null;

  async function handleLog() {
    if (logging || isLimitReached || weekLocked) return;
    if (needsModal) {
      onRequestModal(job);
      return;
    }
    setLogging(true);
    try {
      await weekLogService.logJob(job, "", selectedDate);
    } finally {
      setLogging(false);
    }
  }

  const isDisabled = isLimitReached || logging || weekLocked;

  return (
    <button
      type="button"
      data-testid="job-button"
      disabled={isDisabled}
      onClick={handleLog}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "14px 16px",
        width: "100%",
        textAlign: "left",
        borderBottom: isLast ? "none" : "1px solid var(--line)",
        opacity: isDisabled ? 0.45 : 1,
        cursor: isDisabled ? "default" : "pointer",
        background: "var(--surface)",
      }}
    >
      <div
        style={{ fontSize: 22, width: 32, textAlign: "center", flexShrink: 0 }}
      >
        {job.data.emoji}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 15 }}>{job.data.title}</div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginTop: 2,
          }}
        >
          {job.data.price !== null && (
            <span
              className="num"
              style={{ fontSize: 12, color: "var(--ink-3)", fontWeight: 600 }}
            >
              +{job.data.price} kr
            </span>
          )}
          {limitLabel && (
            <span
              data-testid="job-limit"
              style={{
                fontSize: 11,
                color: isLimitReached ? "var(--coral)" : "var(--ink-3)",
                fontWeight: 600,
              }}
            >
              {limitLabel}
            </span>
          )}
        </div>
      </div>
      {loggedToday && !isLimitReached && (
        <div
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: "var(--good)",
            flexShrink: 0,
          }}
        />
      )}
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          background: isLimitReached ? "var(--bg-2)" : "var(--info)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Plus size={16} color={isLimitReached ? "var(--ink-3)" : "#fff"} />
      </div>
    </button>
  );
}
